import { Box, Tooltip, Typography } from "@mui/material";
import { type MRT_ColumnDef } from "material-react-table";
import { Link } from 'react-router-dom';
import {
  capitalizeFirstLetter,
  validateDate,
  formatCurrency,
  calculateDurationObject,
} from "../../utils/utils";
import "../../styles/common.css"

const initialSort = { sort_field: "domain", sort_order: "asc" };
const displayValue = (val: any) => {
  if (val === null || val === undefined || val === "") return "---";
  return val;
};

const getColumns = (): MRT_ColumnDef<any>[] => [
   {
    accessorKey: "title",
    header: "Interaction title",
    Cell: ({ cell, row }: { cell: any; row: any }) => {
    return (
        <Box className="flex flex-row gap-1 justify-between items-start w-full">
        <Tooltip
                placement="right" title={undefined}            >
            <Link
            color="primary"
            to={`/meeting/${row.original.meeting_id}?${(() => {
              const params = new URLSearchParams();
            
              if (row.original?.isTeamMeeting) {
                params.set("ti", "1"); // is_team_meeting_id
              }
              if (row.original?.isClientInteraction) {
                params.set("ci", "1"); // isClientInteraction
              }
              return params.toString();
            })()}`}
            state={{
                startEndTime: row.original.startEndTime, 
                parent_page_identifier: "client-interaction-overview",
                isSlideOverOpen: row.original?.isSlideOverOpen,
                slideOverParent: row.original?.slideOverParent,
                is_team_meeting_id: row.original.isTeamMeeting,
                isClientInteraction: row.original?.isClientInteraction,
                selectedFilter: row.original?.selectedFilter,
                clientName: row?.original?.clientName,
            }}
            style={{
                flexGrow: 0,
                flexShrink: 1,
                flexBasis: "auto",
                textDecoration: "underline",
                textDecorationColor: "rgba(25, 118, 210, 0.4)",
                color: "#1976d2",
                whiteSpace: "nowrap",
            }}
            className="truncate-table-cell-ellipsis"
            >
                {(cell.getValue() as string)}
            </Link>
        </Tooltip>
        </Box>
    );
    },
    enableColumnFilter: false,
    enableSorting: false,
    grow: true,
    minSize: 300,
    size: 300,
  },
  {
    accessorKey: "clientName",
    header: "Client Name",
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const clientName = cell.getValue();
      const formattedName = capitalizeFirstLetter(clientName);
      const clientDomain = row.original.clientDomain;
      const clientOrgId = row.original?.clientOrgId;
      const subOrgId = row.original?.subOrgId;
      const handleClientClick = () => {
        row.original?.onClientClick(clientDomain, clientName, true, clientOrgId, subOrgId);
      };
      return <Box className="flex flex-row gap-1 justify-center"
        sx={{
          justifyContent: "flex-start",
          alignItems: "flex-start",
          alignContent: "flex-start",
          padding: "4px 8px",
        }}
      >
        <span
        style={{
            flexGrow: 0,
            flexShrink: 1,
            flexBasis: "auto",
            textDecoration: "underline",
            textDecorationColor: "rgba(25, 118, 210, 0.4)",
            color: "#1976d2",
            cursor: "pointer",
          }}
          onClick={handleClientClick}
        >
          {displayValue(formattedName)}
        </span>
      </Box>
    },
    enableColumnFilter: false,
    enableSorting: true,
    grow: true,
    minSize: 150,
    size: 150,
  },
  {
    accessorKey: "totalMrr",
    header: "MRR",
    Cell: ({ cell }: { cell: any }) => {
      return <Box>{displayValue(formatCurrency(cell.getValue() || 0))}</Box>;
    },
    enableColumnFilter: false,
    enableSorting: false,
    grow: true,
    minSize: 110,
    size: 110,
  },
  {
    accessorKey: "date",
    header: "Date",
    filterVariant: "date",
    filterFn: "lessThan",
    sortingFn: "datetime",
    Cell: ({ cell }: { cell: any }) => {
      return (
        <div>
          <span>{validateDate(cell.getValue(), "MMM DD, YYYY")}</span>
        </div>
      );
    },
    grow: true,
    minSize: 100,
    size: 100,
  },
  {
    accessorKey: "duration",
    header: "Duration",
    enableColumnFilter: false,
    Cell: ({ cell }: { cell: any }) => {
      const timeSpend = calculateDurationObject(
        cell.getValue() as number
      ) || {
        days: 0,
        hours: 0,
        minutes: 0,
      };
      return (
        <span>
          {timeSpend.days > 0 &&
            `${timeSpend.days} ${timeSpend.days === 1 ? "day" : "days"}`}
          {timeSpend.hours > 0 &&
            ` ${timeSpend.hours} ${timeSpend.hours === 1 ? "hr" : "hrs"}`}
          {timeSpend.minutes > 0 && ` ${timeSpend.minutes} mins`}
        </span>
      );
    },
    grow: true,
    minSize: 110,
    size: 120
  },
  {
    accessorKey: "rating",
    header: "Rating",
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const sentiment = cell.getValue();
      const isPreviousAsset = row.original?.isPreviousAsset;
      const isPresentAsset = row.original?.isPresentAsset;
        let color;

        if(isPreviousAsset || isPresentAsset) {
          return (
            <Typography className="meeting-sentiment__no-recording">
              Recording Present
            </Typography>
          );
        }
        // If sentiment is null, return "No recording" or any fallback text
        if (sentiment === null) {
            return <Typography sx={{ fontSize: "13px" }}>No recording</Typography>;
        }

        // Set color based on sentiment
        switch (sentiment) {
            case "Positive":
                color = "#04BD00"; // Green
                break;
            case "Negative":
                color = "#F4442F"; // Red
                break;
            case "Neutral":
                color = "#FFCD2A"; // Orange for neutral
                break;
            default:
                return <Typography>No recording</Typography>; // Default for unexpected values
        }

        return (
            <Box
                sx={{
                  width: 14,        // Increased circle size
                  height: 14,
                  backgroundColor: color,
                  borderRadius: "3px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "25px",
                  textAlign: "center",  // Ensure content inside the circle is centered
                  lineHeight: "24px", // Make sure the text is vertically centered
                }}
                title={sentiment}
            />
        );
    },
    enableSorting: false,
    grow: true,
    minSize: 180,
    size: 180,
  },
  {
    accessorKey: "analysis_source",
    header: "Rating Source",
    Cell: ({ cell }: { cell: any }) => {
      const rowData = cell.row.original; // Get the entire row data
      let ratingSource = rowData.analysis_source;
      if (ratingSource === "AiBased") {
          ratingSource = "AI Based";
      } else {
          ratingSource = ratingSource || "NA"; // Fallback value
      }
      return (

        <Typography>{ratingSource}</Typography>

      );
    },
    enableColumnFilter: false,
    enableSorting: false,
    grow: true,
    minSize: 160,
    size: 120,
  },  
];
export { getColumns, initialSort };